// =============================================================================
// CreateBill/membershipPricing.js
// Shared rules for prepaid cards + discount memberships on the billing screen.
// =============================================================================

export const LOW_BALANCE_LIMIT = 500;   // ₹: show "Low balance" below this
export const EXPIRY_WARN_DAYS = 15;     // show "Expires in N days" within this window

const SERVICE_TYPES = ['service', 'add-on', 'addon', 'combo'];
const DAY_MS = 24 * 60 * 60 * 1000;

const lower = (v) => String(v || '').toLowerCase();

export const isServiceRow = (row) => SERVICE_TYPES.includes(lower(row?.itemType));
export const isProductRow = (row) => lower(row?.itemType) === 'product';

export const daysLeft = (date) => {
    if (!date) return null;
    const t = new Date(date).getTime();
    if (Number.isNaN(t)) return null;
    return Math.ceil((t - Date.now()) / DAY_MS);
};

/** Discount % a membership gives to one bill row */
export const membershipDiscountFor = (row, membership) => {
    if (!membership) return 0;
    if (isProductRow(row)) return Number(membership.productDiscount) || 0;
    if (isServiceRow(row)) return Number(membership.serviceDiscount) || 0;
    return 0;
};

/**
 * Re-applies the chosen membership to bill rows.
 * - Rows the staff discounted by hand (discountSource: 'manual') are never touched.
 * - Rows discounted by the previous membership, or with no discount, get the new %.
 */
export const applyMembershipToItems = (items = [], membership) => items.map((row) => {
    if (!String(row.itemName || '').trim()) return row;
    if (row.discountSource === 'manual') return row;
    if (!isServiceRow(row) && !isProductRow(row)) return row;

    const fromMembership = row.discountSource === 'membership';
    const noDiscount = !(Number(row.discountValue) > 0);
    if (!fromMembership && !noDiscount) return row;

    const pct = membershipDiscountFor(row, membership);
    return {
        ...row,
        discountValue: pct > 0 ? pct : '',
        discountType: '%',
        discountSource: pct > 0 ? 'membership' : undefined,
    };
});

/** Active discount memberships, most expensive first (that one is applied by default) */
export const sortMemberships = (discounts = []) =>
    discounts
        .filter((d) => d.status === 'ACTIVE' && (daysLeft(d.expiryDate) ?? 1) > 0)
        .sort((a, b) => (Number(b.price) || 0) - (Number(a.price) || 0)
            || (Number(b.serviceDiscount) || 0) - (Number(a.serviceDiscount) || 0));

/** Same shape for cards from the wallet API or the old customer search */
export const normaliseCards = (wallet, customer) => {
    if (wallet?.cards) {
        return wallet.cards.map((c) => ({
            id: String(c.id),
            name: c.name || 'Prepaid card',
            number: String(c.number || ''),
            balance: Number(c.balance) || 0,
            expiryDate: c.expiryDate || null,
            status: c.status,
        }));
    }
    return (customer?.activePrepaidCards || []).map((c) => ({
        id: String(c._id || c.id),
        name: c.name || 'Prepaid card',
        number: String(c.cardNumber || ''),
        balance: Number(c.currentBalance) || 0,
        expiryDate: c.expiryDate || null,
        status: c.status || 'ASSIGNED',
    }));
};

export const isCardUsable = (card) =>
    card.status === 'ASSIGNED' && card.balance > 0 && (daysLeft(card.expiryDate) ?? 1) > 0;